import ArrowBack from "@mui/icons-material/ArrowBack";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useCallback, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useToken } from "../../../../../../server/auth/use-token";
import { useDialog } from "../../../../../../utils/hooks/use-dialog";
import { serviceURL } from "../../../../../../utils/service-url";
import { uuidv4 } from "../../../../../../utils/uuid";
import { ContentLibraryAssets } from "../../../../../assets/components/content-library-assets";
import { FolderBreadcrumb } from "../../../../../assets/components/folder-breadcrumb";
import { useFolders } from "../../../../../assets/hooks/use-folders";
import {
  contentLibraryReducer,
  filterAssets,
  initialState,
} from "../../../../../assets/reducer/reducer";
import { UploadNewFileModal } from "../../../../../assets/upload-new-file-modal";
import {
  ROOT_ID,
  constructBreadcrumbs,
  transformFoldersToTreeItems,
} from "../../../../../assets/utils";
import { usePublishDesignHuddleTemplateWithTags } from "../../../../../design-huddle/use-publish-template-with-tags";
import { copyImage } from "../../../../../design-huddle/utils";
import { useContentTypeMultipage } from "../../../../../multiformat/hooks/use-content-type-multipage";
import { SearchField } from "../../../../../search/search-field";
import { AssetPreview } from "./asset-preview";
import { InsertImageButton } from "./insert-image-button";
import { InsertAsLinkButton } from "./insert-link-button";
import { InsertVideoButton } from "./insert-video-button";

import type { UUID } from "../../../../../../utils/uuid";
import type { Asset } from "../../../../../assets/server/digital-asset-service";
import type { Folder } from "../../../../../assets/server/folder-service";
import type { TreeViewItem } from "../../../../../assets/utils";
import type { NodeKey } from "lexical";
import type { FC } from "react";

export const ContentLibraryModal: FC<{
  open: boolean;
  onClose: () => void;
  assets: Asset[];
  nodeKey?: NodeKey;
  onUseAsLink?: (url: string, title: string, asset: Asset) => void;
  onAssetUploaded?: (folderId?: string | null) => void;
  initialFolderId?: string | null;
  useRawAssetPublicUrlAsLink?: boolean;
}> = ({
  open,
  onClose,
  assets,
  nodeKey,
  onUseAsLink,
  onAssetUploaded,
  initialFolderId,
  useRawAssetPublicUrlAsLink,
}) => {
  const getToken = useToken();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const uploadModal = useDialog();

  const [state, dispatch] = useReducer(contentLibraryReducer, {
    ...initialState,
    sidebarOpen: false,
    selectedTreeItemId: initialFolderId ?? null,
  });

  const { data: foldersData, loading: foldersLoading, reload: reloadFolders } = useFolders();

  const { data: multipage } = useContentTypeMultipage(selectedAsset?.content_type);
  const publishTags = usePublishDesignHuddleTemplateWithTags();

  const treeItems = useMemo<TreeViewItem[]>(() => {
    if (!foldersData) return [];
    return transformFoldersToTreeItems(foldersData);
  }, [foldersData]);

  const filteredData = useMemo(
    () =>
      filterAssets(
        assets,
        {
          selectedTreeItemId: state.selectedTreeItemId,
          appliedContentTypes: state.appliedContentTypes,
          appliedTags: state.appliedTags,
          searchText: state.searchText,
        },
        treeItems,
      ),
    [assets, state, treeItems],
  );

  const filteredAllData = useMemo(
    () =>
      filterAssets(
        assets,
        {
          selectedTreeItemId: null,
          appliedContentTypes: state.appliedContentTypes,
          appliedTags: state.appliedTags,
          searchText: state.searchText,
        },
        treeItems,
      ),
    [assets, state, treeItems],
  );

  const filteredFolders = useMemo<Folder[]>(() => {
    if (!foldersData) return [];
    const folderId =
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      state.selectedTreeItemId === ROOT_ID ? null : (state.selectedTreeItemId as UUID | null);
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return foldersData
      .filter((folder) => folder.parent_folder_id === folderId)
      .filter((f) => f.name.toLowerCase().includes(state.searchText.trim().toLowerCase()))
      .sort((a, b) => naturalCollator.compare(a.name, b.name));
  }, [foldersData, state.searchText, state.selectedTreeItemId]);

  const filteredAllFolders = useMemo<Folder[]>(() => {
    if (!foldersData || !state.searchText.trim()) return [];
    const searchLower = state.searchText.trim().toLowerCase();
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return foldersData
      .filter((folder) => folder.name.toLowerCase().includes(searchLower))
      .sort((a, b) => naturalCollator.compare(a.name, b.name));
  }, [foldersData, state.searchText]);

  const breadcrumbs = useMemo(
    () => constructBreadcrumbs(treeItems, state.selectedTreeItemId),
    [treeItems, state.selectedTreeItemId],
  );

  const handleUseAssetAsLink = useCallback(async () => {
    if (!onUseAsLink || !selectedAsset) return;

    setLoading(true);
    if (!selectedAsset.public_url) {
      setLoading(false);
      return null;
    }

    if (useRawAssetPublicUrlAsLink) {
      onUseAsLink(selectedAsset.public_url, selectedAsset.title, selectedAsset);
      onClose();
      setLoading(false);
      return;
    }

    if (selectedAsset.content_type === "Uploaded File") {
      onUseAsLink(selectedAsset.public_url, selectedAsset.title, selectedAsset);
      onClose();
      setLoading(false);
      return;
    }

    const template = selectedAsset.project_id && (await publishTags(selectedAsset.project_id));
    if (template) {
      const params =
        template.mergeTags.length > 0
          ? `?${template.mergeTags.map((tag) => `${tag}={{${tag}}}`).join("&")}`
          : "";
      const templateUrl = `${serviceURL("huddle")}template/${template.templateCode}/preview${params}`;
      onUseAsLink(templateUrl, selectedAsset.title, selectedAsset);
      onClose();
      setLoading(false);
      return;
    }

    const response = await copyImage(getToken, selectedAsset.public_url, uuidv4());
    if (!response.success) {
      toast.error("Failed to use image as link");
      setLoading(false);
      return null;
    }

    onUseAsLink(response.url, selectedAsset.title, selectedAsset);
    onClose();
    setLoading(false);
  }, [getToken, onClose, onUseAsLink, selectedAsset, publishTags, useRawAssetPublicUrlAsLink]);

  const handleSelectAsset = useCallback(
    (asset: Asset) => {
      if (asset.id === selectedAsset?.id) {
        setPreviewAsset(asset);
      } else {
        setSelectedAsset(asset);
      }
    },
    [selectedAsset],
  );

  const handlePreviewAsset = useCallback((asset: Asset) => {
    setSelectedAsset(asset);
    setPreviewAsset(asset);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: "SET_SEARCH_TEXT", payload: e.target.value });
    },
    [dispatch],
  );

  const handleOnClickFolder = useCallback(
    (folderId: string) => {
      dispatch({ type: "SELECT_FOLDER", payload: folderId });
      if (!state.expandedItems.includes(folderId)) {
        dispatch({ type: "SET_EXPANDED_ITEMS", payload: [...state.expandedItems, folderId] });
      }
    },
    [dispatch, state.expandedItems],
  );

  // set initial modal height so that it is retained after filtering is applied
  useLayoutEffect(() => {
    if (open) {
      setTimeout(() => {
        if (contentRef.current && contentHeight === null) {
          setContentHeight(contentRef.current.offsetHeight);
        }
      }, 100);
    }
  }, [contentHeight, open]);

  if (assets.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="lg">
        <DialogTitle mt={2}>Insert</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            You haven’t saved any content yet. Visit your Content Library to do so.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="editor-content-empty-library-cancel"
            type="button"
            onClick={onClose}
            loading={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (previewAsset && reason === "backdropClick") return;
        onClose();
      }}
      fullWidth={true}
      maxWidth={previewAsset ? "md" : "lg"}
    >
      {previewAsset ? (
        <>
          <DialogTitle mt={2}>Insert</DialogTitle>
          <Box ml={2}>
            <Button
              startIcon={<ArrowBack />}
              data-analytics-id="editor-content-library-back"
              onClick={() => setPreviewAsset(null)}
            >
              Back
            </Button>
          </Box>
          <DialogContent>
            <Box mb={2}>
              <Typography variant="h6">{previewAsset.title}</Typography>
            </Box>
            <AssetPreview
              asset={previewAsset}
              height="300px"
              iconFontSize={150}
              showPreviewIcon={false}
              videoPlayer={true}
            />
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle my={2}>Content Library</DialogTitle>
          <DialogContent
            ref={contentRef}
            sx={{ height: contentHeight ? `${contentHeight}px` : "auto", overflowY: "auto" }}
          >
            <Box sx={{ display: "flex", minHeight: 1200 }}>
              <Box sx={{ flex: 1 }}>
                {foldersLoading && <CircularProgress />}
                {!foldersLoading && (
                  <ContentLibraryAssets
                    allFolders={foldersData ?? undefined}
                    filteredData={filteredData}
                    allFilteredData={filteredAllData}
                    filteredFolders={filteredFolders}
                    allFilteredFolders={filteredAllFolders}
                    onClickFolder={handleOnClickFolder}
                    state={state}
                    selectedAsset={selectedAsset}
                    hideFolderActions={true}
                    slots={{
                      breadcrumb: (
                        <FolderBreadcrumb
                          breadcrumbs={breadcrumbs}
                          onFolderSelect={handleOnClickFolder}
                        />
                      ),
                      searchField: (
                        <SearchField
                          label="Search"
                          defaultValue={state.searchText}
                          onChange={handleSearchChange}
                          fullWidth
                        />
                      ),
                      uploadButton: (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<FileUploadIcon />}
                          data-analytics-id="content-library-modal-upload-file"
                          onClick={() => uploadModal.handleOpen()}
                          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                        >
                          Upload file
                        </Button>
                      ),
                    }}
                    onSelectAsset={handleSelectAsset}
                    onPreviewAsset={handlePreviewAsset}
                    onClearSelection={() => {}}
                    onArchiveSelectedAssets={() => {}}
                    onCreateFolder={() => {}}
                    onArchiveFolder={() => {}}
                    onMoveFolder={() => {}}
                  />
                )}
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
      <DialogActions>
        <Button
          data-analytics-id="editor-content-library-cancel"
          type="button"
          onClick={onClose}
          loading={loading}
        >
          Cancel
        </Button>
        {previewAsset && (
          <InsertAsLinkButton
            asset={selectedAsset}
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
          />
        )}
        {previewAsset && !onUseAsLink && !multipage && (
          <InsertImageButton
            buttonText="Insert image"
            asset={selectedAsset}
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
            nodeKey={nodeKey}
            disabled={!selectedAsset}
            onInsert={() =>
              selectedAsset
                ? new Promise((resolve) => resolve({ success: true, id: selectedAsset.id }))
                : Promise.resolve({ success: false })
            }
          />
        )}
        {previewAsset && !onUseAsLink && (
          <InsertVideoButton
            asset={selectedAsset}
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
          />
        )}
        {!previewAsset && !onUseAsLink && (
          <Button
            data-analytics-id="editor-content-library-preview-image"
            type="button"
            disabled={selectedAsset === null}
            onClick={() => {
              setPreviewAsset(selectedAsset);
            }}
          >
            Insert
          </Button>
        )}
        {onUseAsLink && (
          <Button
            data-analytics-id="editor-content-library-use-link"
            type="button"
            disabled={selectedAsset === null}
            loading={loading}
            onClick={() => {
              if (selectedAsset) {
                void handleUseAssetAsLink();
              }
            }}
          >
            Use as link
          </Button>
        )}
      </DialogActions>
      <UploadNewFileModal
        key={`upload-modal-${uploadModal.open ? "open" : "closed"}`}
        open={uploadModal.open}
        onOpen={uploadModal.handleOpen}
        onClose={uploadModal.handleClose}
        onCancel={uploadModal.handleClose}
        onChange={(folderId) => {
          onAssetUploaded?.(folderId);
          if (folderId) {
            dispatch({ type: "SELECT_FOLDER", payload: folderId });
            reloadFolders();
          }
        }}
        onViewFileClick={(asset) => {
          setSelectedAsset(asset);
        }}
        folderId={
          state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
            ? // oxlint-disable-next-line typescript/no-unsafe-type-assertion
              (state.selectedTreeItemId as UUID)
            : null
        }
      />
    </Dialog>
  );
};
